import { useCallback, useContext } from 'react';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { updateBillingInfoMutation } from '../graphql-operations';
import CheckoutContext from '../lib/CheckoutContext';
import ErrorMessages, { getCustomerMessageFromApolloError, logInternalError } from '../lib/errors';
import { fetchRecaptchaToken } from '../lib/recaptcha';
import useCheckoutPaths from './useCheckoutPaths';
import * as events from '../lib/analytics/events';
import * as types from '../types';

interface UsePayPalBillingOptions {
  onError: (message: string) => void;
}

/**
 * Shared PayPal billing flow used by both the cart page and stripe checkout page.
 * Handles recaptcha, billing mutation, analytics, and navigation to shipping.
 */
export default function usePayPalBilling({ onError }: UsePayPalBillingOptions) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { checkoutActions, session } = useContext(CheckoutContext);
  const checkoutPaths = useCheckoutPaths();

  const [updateBilling, { loading }] = useMutation<
    types.gqlTypes.updateBillingInfo,
    types.gqlTypes.updateBillingInfoVariables
  >(updateBillingInfoMutation);

  const onComplete = useCallback(
    (token: types.BillingToken) => {
      if (loading) return;

      if (session) {
        fetchRecaptchaToken('payment_add')
          .catch((recaptchaErr) => {
            logInternalError(recaptchaErr, {
              fingerprint: ['{{ default }}', 'recaptcha-paypal-billing'],
              tags: { paymentMethod: 'paypal', checkoutStep: 'recaptcha' },
            });
            return undefined;
          })
          .then((recaptchaToken) =>
            updateBilling({ variables: { input: { billingInfo: { token: token.id } }, recaptchaToken } }),
          )
          .then((result) => {
            const newBillingInfo = result.data?.updateBillingAccount?.billingInfo;
            if (newBillingInfo) {
              dispatch(checkoutActions.setBillingInfo({ billingInfo: newBillingInfo }));
              events.payment.paypalTokenSuccess();
              history.push(checkoutPaths.Shipping);
            } else {
              throw new Error('No updated billing information');
            }
          })
          .catch((err) => {
            const customerMessage = getCustomerMessageFromApolloError(err);
            if (customerMessage) {
              onError(customerMessage);
            } else {
              logInternalError(err, {
                fingerprint: ['{{ default }}', err.message],
                tags: { paymentMethod: 'paypal', checkoutStep: 'updateBillingInfo' },
              });
              onError(ErrorMessages.DEFAULT);
            }
            events.payment.paypalTokenError(err.message);
          });
      } else {
        events.payment.paypalTokenSuccess();
        history.push(checkoutPaths.Shipping, { billingToken: token.id });
      }
    },
    [loading, session, updateBilling, dispatch, checkoutActions, history, checkoutPaths.Shipping, onError],
  );

  return { onComplete, loading };
}
